import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getApiUrl } from '../config/api';
import { useConfirmDialog } from '../context/ConfirmContext';
import { useToast } from '../context/ToastContext';
import styles from '../styles/modules/MyAvailability.module.css';
import headerStyles from '../styles/modules/Header.module.css';

interface Availability {
    _id: string;
    userId: string;
    date: string;
    startTime: string;
    place: string;
    needsTransport: boolean;
    status: string;
    notes?: string;
    createdAt: string;
    updatedAt: string;
}

const MyAvailability: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();
    const { confirmDelete } = useConfirmDialog();
    const { showToast } = useToast();
    const [availabilities, setAvailabilities] = useState<Availability[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set());

    useEffect(() => {
        fetchMyAvailabilities();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const state = location.state as { toast?: { message: string; type: 'success' | 'error' | 'info' } } | null;
        if (state?.toast) {
            showToast(state.toast);
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [location.pathname, location.state, navigate, showToast]);

    const fetchMyAvailabilities = async () => {
        const token = localStorage.getItem('authToken');
        if (!token) {
            showToast({ message: t('common.loginRequired'), type: 'error' });
            navigate('/');
            return;
        }

        setLoading(true);
        setError('');

        try {
            const response = await fetch(getApiUrl('availability/my'), {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                if (response.status === 401) {
                    localStorage.removeItem('authToken');
                    localStorage.removeItem('user');
                    navigate('/');
                    return;
                }
                throw new Error(`Failed to fetch availabilities: ${response.statusText}`);
            }

            const data = await response.json();
            setAvailabilities(data);
            console.log('Availabilities:', data);
        } catch (error) {
            console.error('Error fetching availabilities:', error);
            const errorMessage = error instanceof Error ? error.message : t('availability.fetchFailed');
            setError(errorMessage);
            showToast({ message: errorMessage, type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteAvailability = async (availabilityId: string) => {
        const confirmed = await confirmDelete({
            title: t('common.confirm'),
            message: t('messages.deleteConfirm'),
            confirmLabel: t('common.delete'),
            cancelLabel: t('common.cancel')
        });
        if (!confirmed) {
            return;
        }

        const token = localStorage.getItem('authToken');
        if (!token) {
            showToast({ message: t('common.loginRequired'), type: 'error' });
            return;
        }

        try {
            const response = await fetch(getApiUrl(`availability/${availabilityId}`), {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                throw new Error('Failed to delete availability');
            }

            // Remove from state
            setAvailabilities(prev => prev.filter(avail => avail._id !== availabilityId));
            showToast({ message: t('availability.deleteSuccess'), type: 'success' });
        } catch (error) {
            console.error('Error deleting availability:', error);
            showToast({
                message: error instanceof Error ? error.message : t('availability.deleteFailed'),
                type: 'error'
            });
        }
    };

    const handleStatusChange = async (availabilityId: string, newStatus: string) => {
        const token = localStorage.getItem('authToken');
        if (!token) {
            showToast({ message: t('common.loginRequired'), type: 'error' });
            return;
        }

        try {
            const response = await fetch(getApiUrl(`availability/${availabilityId}/status`), {
                method: 'PATCH',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ status: newStatus })
            });

            if (!response.ok) {
                throw new Error('Failed to update status');
            }

            const updatedAvailability = await response.json();
            
            // Update state
            setAvailabilities(prev => prev.map(avail => 
                avail._id === availabilityId ? { ...avail, status: updatedAvailability.status } : avail
            ));
        } catch (error) {
            console.error('Error updating status:', error);
            showToast({
                message: error instanceof Error ? error.message : t('availability.statusUpdateFailed'),
                type: 'error'
            });
        }
    };

    const handleBackClick = () => {
        navigate('/dashboard');
    };

    const handleAddNew = () => {
        navigate('/add-availability');
    };

    const toggleDescription = (availabilityId: string) => {
        setExpandedCards(prev => {
            const newSet = new Set(prev);
            if (newSet.has(availabilityId)) {
                newSet.delete(availabilityId);
            } else {
                newSet.add(availabilityId);
            }
            return newSet;
        });
    };

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
        });
    };

    return (
        <div className={styles.myAvailabilityPage}>
            <div className={styles.pageHeader}>
                <button className={headerStyles.backButton} onClick={handleBackClick}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M19 12H5M12 19l-7-7 7-7"/>
                    </svg>
                </button>
                <h1 className={styles.headerTitle}>{t('availability.title')}</h1>
                <button className={styles.actionButton} onClick={handleAddNew}>
                    {t('availability.createNew')}
                </button>
            </div>

            <div className={styles.myAvailabilityContainer}>
                <div className={styles.myAvailabilityContent}>
                    {loading ? (
                        <div className={styles.loading}>{t('common.loading')}</div>
                    ) : error ? (
                        <div className={styles.errorMessage}>{error}</div>
                    ) : availabilities.length === 0 ? (
                        <div className={styles.noResults}>
                            <p>{t('availability.noAvailabilityMessage')}</p>
                            <button className={styles.createButton} onClick={handleAddNew}>
                                {t('availability.createFirst')}
                            </button>
                        </div>
                    ) : (
                        <div className={styles.availabilitiesGrid}>
                            {availabilities.map((availability) => (
                                <div key={availability._id} className={styles.availabilityCard}>
                                    <div className={styles.cardHeader}>
                                        <div className={styles.dateInfo}>
                                            <span className={styles.date}>{formatDate(availability.date)}</span>
                                            <span className={styles.time}>{availability.startTime}</span>
                                        </div>
                                        <select 
                                            className={`${styles.statusDropdown} ${styles[`status${availability.status.charAt(0).toUpperCase() + availability.status.slice(1).toLowerCase()}`]}`}
                                            value={availability.status}
                                            onChange={(e) => handleStatusChange(availability._id, e.target.value)}
                                        >
                                            <option value="available">{t('availability.available')}</option>
                                            <option value="booked">{t('availability.booked')}</option>
                                            <option value="unavailable">{t('availability.cancelled')}</option>
                                        </select>
                                    </div>

                                    <div className={styles.cardBody}>
                                        <div className={styles.infoRow}>
                                            <span className={styles.label}>📍 {t('availability.location')}:</span>
                                            <span className={styles.value}>{availability.place}</span>
                                        </div>

                                        <div className={styles.infoRow}>
                                            <span className={styles.label}>🚗 {t('searchStaff.transport')}:</span>
                                            <span className={styles.value}>
                                                {availability.needsTransport ? t('searchStaff.transportNeeded') : t('searchStaff.noTransportNeeded')}
                                            </span>
                                        </div>

                                        {availability.notes && (
                                            <div className={styles.notesSection}>
                                                <button 
                                                    className={styles.notesToggle}
                                                    onClick={() => toggleDescription(availability._id)}
                                                >
                                                    <span>📝 {t('availability.notes')}</span>
                                                    <svg 
                                                        width="16" 
                                                        height="16" 
                                                        viewBox="0 0 24 24" 
                                                        fill="none" 
                                                        stroke="currentColor" 
                                                        strokeWidth="2"
                                                        style={{
                                                            transform: expandedCards.has(availability._id) ? 'rotate(180deg)' : 'rotate(0deg)',
                                                            transition: 'transform 0.3s ease'
                                                        }}
                                                    >
                                                        <path d="M6 9l6 6 6-6"/>
                                                    </svg>
                                                </button>
                                                {expandedCards.has(availability._id) && (
                                                    <div className={styles.notesContent}>
                                                        {availability.notes}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    <div className={styles.cardFooter}>
                                        <button 
                                            className={styles.deleteButton}
                                            onClick={() => handleDeleteAvailability(availability._id)}
                                        >
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/>
                                            </svg>
                                            {t('common.delete')}
                                        </button>
                                        <span className={styles.createdDate}>
                                            {t('availability.postedOn')}: {new Date(availability.createdAt).toLocaleDateString()}
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

        </div>
    );
};

export default MyAvailability;
